import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Toast } from "@aurem/ui";
import { useSearchParams } from "react-router-dom";
import { gql } from "types";

const ACCEPT_USER_INVITATION = gql(/* GraphQL */ `
  mutation AcceptUserInvitation($invitationId: String!) {
    acceptUserInvitation(invitationId: $invitationId) {
      id
      oldRole
      company {
        id
        name
      }
      role {
        name
      }
    }
  }
`);

const ACCEPT_EMPLOYEE_INVITATION = gql(/* GraphQL */ `
  mutation AcceptEmployeeInvitation($invitationId: String!) {
    acceptEmployeeInvitation(invitationId: $invitationId) {
      id
      employments {
        id
        company {
          id
          name
        }
      }
    }
  }
`);

export const FIND_USER_INVITATION = gql(/* GraphQL */ `
  query FindUserInvitation($invitationId: String!) {
    findUserInvitation(invitationId: $invitationId) {
      id
    }
  }
`);

export const FIND_EMPLOYEE_INVITATION = gql(/* GraphQL */ `
  query FindEmployeeInvitation($invitationId: String!) {
    findEmployeeInvitation(invitationId: $invitationId) {
      id
    }
  }
`);

type AcceptInvitationProps = {
  accessToken?: string | null | undefined;
  /* Fallback for invitationId search param */
  invitationId?: string | null | undefined;
  /* Fallback for type search param */
  type?: string | null | undefined;
};

export const useAcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const { data: userInvitationData, loading: loadingUserInvitation } = useQuery(
    FIND_USER_INVITATION,
    {
      variables: {
        invitationId: searchParams.get("invitationId") || "",
      },
      skip: !searchParams.get("invitationId"),
    },
  );
  const { data: employeeInvitationData, loading: loadingEmployeeInvitation } =
    useQuery(FIND_EMPLOYEE_INVITATION, {
      variables: {
        invitationId: searchParams.get("invitationId") || "",
      },
      skip: !searchParams.get("invitationId"),
    });
  const [acceptUserInvitation, { loading: loadingUser, error: errorUser }] =
    useMutation(ACCEPT_USER_INVITATION);
  const [
    acceptEmployeeInvitation,
    { loading: loadingEmployee, error: errorEmployee },
  ] = useMutation(ACCEPT_EMPLOYEE_INVITATION);

  const acceptInvitation = async (options?: AcceptInvitationProps) => {
    const invitationId =
      searchParams.get("invitationId") || options?.invitationId;
    const type = searchParams.get("type") || options?.type;

    if (!invitationId) {
      return null;
    }

    const context = options?.accessToken
      ? {
          headers: {
            Authorization: options.accessToken,
          },
        }
      : undefined;

    try {
      if (type === "employee") {
        const result = await acceptEmployeeInvitation({
          variables: { invitationId },
          context,
        });
        return result.data?.acceptEmployeeInvitation ?? null;
      } else {
        const result = await acceptUserInvitation({
          variables: { invitationId },
          context,
        });
        return result.data?.acceptUserInvitation ?? null;
      }
    } catch (error) {
      Toast.error({
        title: (error as ApolloError).message,
      });
    }
  };

  return {
    acceptInvitation,
    invitation:
      userInvitationData?.findUserInvitation ||
      employeeInvitationData?.findEmployeeInvitation ||
      null,
    loading:
      loadingUser ||
      loadingEmployee ||
      loadingUserInvitation ||
      loadingEmployeeInvitation,
    error: errorUser || errorEmployee,
  };
};
